/**
PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 *  Copyright © 2026 Pearson Education, Inc.
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
* */

/**
 * commonStyles
 *
 * @file CommonStyles.jsx
 * @author Gladson Samuel S
 * @since 0.0.20
 */

import mixins from './mixins.scss';

const CommonStyles = () => ({
  /**
 * scrollContainer - Custom scrollbar styling for containers with scrollable content
 *
 * This style object provides cross-browser scrollbar customization:
 * - Firefox: Uses scrollbarWidth and scrollbarColor properties
 * - Webkit browsers (Chrome, Safari, Edge): Uses ::-webkit-scrollbar pseudo-elements
 */
  scrollContainer: ({ scrollbarWidth = 8, scrollbarColor = 'rgba(81, 46, 171, 0.15) transparent' }) => ({
    // Enable vertical scrolling when content overflows
    overflowY: 'auto',
    boxSizing: 'border-box',

    // paddingRight: '12px',
    // marginRight: '4px',

    // Firefox: Set scrollbar width to 'thin' (narrower than default)
    scrollbarWidth: 'thin',

    // Firefox: Set scrollbar colors (thumb color, track color)
    // First color (#512EAB26) is the thumb, second (transparent) is the track
    scrollbarColor,

    /**
     * Webkit browsers (Chrome, Safari, Edge) scrollbar customization
     * ::-webkit-scrollbar targets the entire scrollbar element
     */
    '&::-webkit-scrollbar': {
      width: scrollbarWidth, // Set scrollbar width to 8px
      background: 'transparent' // Make scrollbar background transparent
    },

    /**
     * ::-webkit-scrollbar-track targets the scrollbar track (the area behind the thumb)
     * The track is the "groove" or "channel" that the scrollbar thumb moves along
     */
    '&::-webkit-scrollbar-track': {
      background: 'transparent' // Make track background transparent
    },

    /**
     * ::-webkit-scrollbar-thumb targets the scrollbar thumb (the draggable part)
     * The thumb is the actual scrollbar handle that users drag to scroll
     */
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: scrollbarColor, // Semi-transparent purple color for the thumb
      borderRadius: 8, // Rounded corners for the thumb
      border: 'none' // Remove any border around the thumb
    },

    /**
     * ::-webkit-scrollbar-thumb:hover targets the thumb when hovered
     * Provides visual feedback when user hovers over the scrollbar
     */
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: scrollbarColor // Slightly more opaque purple on hover
    },

    /**
     * ::-webkit-scrollbar-button targets the scrollbar buttons (up/down arrows)
     * These are the small buttons at the top and bottom of the scrollbar
     */
    '&::-webkit-scrollbar-button': {
      display: 'none' // Hide the scrollbar buttons completely
    },

    /**
     * ::-webkit-scrollbar-corner targets the corner where scrollbars meet
     * This is the small square area where horizontal and vertical scrollbars intersect
     */
    '&::-webkit-scrollbar-corner': {
      background: 'transparent' // Make the corner area transparent
    }
  }),
  highlightContainer: {
    transition: 'outline 300ms ease-in-out',
    outline: '2px solid #31B9E1',
    borderRadius: '16px',
    overflow: 'hidden'
  },
  linkBtnText: {
    color: mixins.partOfSubscriptionBGColor,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: `var(--fontFamily, ${mixins.fontFamilyTT})`,
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '22px',
    textDecorationLine: 'underline',
    textDecorationStyle: 'solid',
    textDecorationSkipInk: 'auto',
    textDecorationThickness: '8%',
    textUnderlineOffset: 'auto',
    textUnderlinePosition: 'from-font',

    '&:hover': {
      color: mixins.lightBorderColor1
    }
  }
});

export default CommonStyles;
